import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import { useAuth } from '../context/AuthContext';
import { Star, QrCode, ExternalLink, Lock, Award, X } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import { TIER_COLORS as tierColors } from '../config/tiers';
import './SponsorsPage.css';

const tierOrder = { platinum: 1, gold: 2, silver: 3, standard: 4 };

const SponsorsPage = () => {
  const { clubSlug } = useParams();
  const { user, getMembership, loading: authLoading } = useAuth();
  const membership = getMembership(clubSlug);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [codeData, setCodeData] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState('');

  const { data: club } = useQuery({ queryKey: ['club', clubSlug], queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data) });
  const { data: sponsors = [], isLoading } = useQuery({ queryKey: ['sponsors', clubSlug], queryFn: () => axios.get(`/clubs/${clubSlug}/sponsors`).then(r => r.data) });

  const accentColor = club?.accent_color || '#C4943A';

  // WT-28: the offer modal closes with Esc too (and the ✕ below).
  const closeModal = () => { setSelectedSponsor(null); setCodeData(null); };
  useEffect(() => {
    if (!selectedSponsor) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setSelectedSponsor(null); setCodeData(null); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedSponsor]);

  // v35: for anyone in the club, Wallet is the one place for offers, codes and
  // the club card. This page stays as the public shop window for visitors.
  // SB-7: now for EVERYONE signed in — the Wallet shows non-members and
  // Marakin staff (implicit memberships) the offers to browse, no codes.
  if (user) return <Navigate to={`/${clubSlug}/wallet`} replace />;

  const getMyCode = async (sponsor) => {
    setSelectedSponsor(sponsor);
    setCodeData(null);
    setCodeError('');
    if (!user || !membership) return;
    // B-66: no staff special case here any more. Signed-in people never reach
    // this (they're redirected to the Wallet above, which shows staff their
    // real codes or a labelled preview), and the server decides who may hold
    // a code: GET /:sponsorId/code serves staff with a real membership and
    // refuses staff on the implicit one with its own message.
    setCodeLoading(true);
    try {
      const res = await axios.get(`/clubs/${clubSlug}/sponsors/${sponsor.id}/code`);
      setCodeData(res.data);
    } catch (err) {
      setCodeError(err.response?.data?.error || 'Could not generate code. Try again.');
    } finally { setCodeLoading(false); }
  };

  const sortedSponsors = [...sponsors].sort((a, b) => (tierOrder[a.tier] || 4) - (tierOrder[b.tier] || 4));
  const featured = sortedSponsors.filter(s => s.featured);
  const rest = sortedSponsors.filter(s => !s.featured);

  return (
    <div className="sponsors-page" style={{ '--accent': accentColor }}>
      <Navbar club={club} />

      <PageHeader
        icon={Star}
        title="Sponsor Offers"
        subtitle={<>Discounts from {club?.name}'s sponsors — for everyone at the club</>}
      />

      {/* WT-1 review: no Login / Register while a stored session is still loading */}
      {!user && !authLoading && (
        <div className="sponsors-login-prompt">
          <div className="sponsors-login-inner">
            <Lock size={32} style={{ color: accentColor }} />
            <h3>Offers for everyone at the club</h3>
            <p>Log in or register to get your own QR codes for these sponsor discounts.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              {/* UC-B5: back to this club's offers (the Wallet, once signed in) */}
              <Link to="/login" state={{ from: `/${clubSlug}/wallet` }} className="btn btn-primary">Login</Link>
              <Link to="/register" className="btn btn-secondary">Register</Link>
            </div>
          </div>
        </div>
      )}

      <div className="sponsors-content">
        {isLoading && <div className="page-loading"><div className="spinner" /></div>}

        {featured.length > 0 && (
          <div className="sponsors-section">
            <h2 className="sponsors-section-title"><Award size={18} /> Featured Partners</h2>
            <div className="sponsors-featured-grid">
              {featured.map(sponsor => (
                <SponsorCard key={sponsor.id} sponsor={sponsor} accentColor={accentColor} user={user} membership={membership} onGetCode={getMyCode} />
              ))}
            </div>
          </div>
        )}

        {rest.length > 0 && (
          <div className="sponsors-section">
            <h2 className="sponsors-section-title">All Sponsors</h2>
            <div className="sponsors-grid">
              {rest.map(sponsor => (
                <SponsorCard key={sponsor.id} sponsor={sponsor} accentColor={accentColor} user={user} membership={membership} onGetCode={getMyCode} />
              ))}
            </div>
          </div>
        )}

        {!isLoading && sponsors.length === 0 && (
          <div className="empty-state">No sponsors listed yet.</div>
        )}
      </div>

      {/* QR Code Modal */}
      {selectedSponsor && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-card qr-modal" role="dialog" aria-modal="true" aria-label={selectedSponsor.business_name} onClick={e => e.stopPropagation()}>
            {/* WT-28: a visible way out (it had none; only a tap outside worked). */}
            <button type="button" className="qr-modal-close" aria-label="Close" onClick={closeModal} autoFocus><X size={20} /></button>
            <div className="qr-modal-sponsor">
              <div className="qr-sponsor-logo" style={{ background: `${tierColors[selectedSponsor.tier]}20` }}>
                {selectedSponsor.logo_url ? <img src={selectedSponsor.logo_url} alt={selectedSponsor.business_name} /> : selectedSponsor.business_name.charAt(0)}
              </div>
              <div>
                <div className="badge" style={{ background: `${tierColors[selectedSponsor.tier]}25`, color: tierColors[selectedSponsor.tier], marginBottom: 6 }}>
                  {selectedSponsor.tier}
                </div>
                <h2>{selectedSponsor.business_name}</h2>
                {selectedSponsor.category && <p className="sponsor-category">{selectedSponsor.category}</p>}
              </div>
            </div>

            <div className="qr-offer-box" style={{ background: `${accentColor}10`, border: `1px solid ${accentColor}30` }}>
              <div className="qr-offer-title" style={{ color: accentColor }}>🎁 {selectedSponsor.offer_title}</div>
              <p>{selectedSponsor.offer_description}</p>
              <div className="qr-offer-value">{selectedSponsor.offer_value}</div>
            </div>

            {authLoading ? (
              <div className="qr-loading"><div className="spinner" /></div>
            ) : !user || !membership ? (
              <div className="qr-locked">
                <Lock size={20} />
                <p>{user ? `Join ${club?.name} to get your own code.` : `Log in and join ${club?.name} to get your own code.`}</p>
                {!user && <Link to="/login" state={{ from: `/${clubSlug}/wallet` }} className="btn btn-primary" style={{ marginTop: 12 }}>Login to access</Link>}
              </div>
            ) : codeLoading ? (
              <div className="qr-loading"><div className="spinner" /><p>Generating your unique code…</p></div>
            ) : codeError ? (
              <div className="qr-error">{codeError}</div>
            ) : codeData ? (
              <div className="qr-code-display">
                <p className="qr-instruction">Show this QR code at {selectedSponsor.business_name} to redeem your offer.</p>
                <div className="qr-image-wrap">
                  <img src={codeData.qr_image} alt="Your QR code" className="qr-image" />
                </div>
                <div className="qr-code-text">{codeData.code}</div>
                <p className="qr-note">⚠️ This code is unique to you and can only be used once.</p>
                {codeData.expires_at && (
                  <p className="qr-expiry">Expires: {new Date(codeData.expires_at).toLocaleString()}</p>
                )}
              </div>
            ) : null}

            {selectedSponsor.website && (
              <a href={selectedSponsor.website} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ marginTop: 16, justifyContent: 'center' }}>
                <ExternalLink size={14} /> Visit {selectedSponsor.business_name}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SponsorCard = ({ sponsor, accentColor, user, membership, onGetCode }) => (
  <div className="sponsor-card card">
    <div className="sponsor-card-top">
      <div className="sponsor-logo" style={{ background: `${tierColors[sponsor.tier]}15` }}>
        {sponsor.logo_url ? <img src={sponsor.logo_url} alt={sponsor.business_name} /> : sponsor.business_name.charAt(0)}
      </div>
      <div className="sponsor-info">
        <div className="sponsor-tier-row">
          <span className="badge" style={{ background: `${tierColors[sponsor.tier]}20`, color: tierColors[sponsor.tier] }}>{sponsor.tier}</span>
          {sponsor.category && <span className="sponsor-cat">{sponsor.category}</span>}
        </div>
        <h3>{sponsor.business_name}</h3>
        {sponsor.description && <p className="sponsor-desc">{sponsor.description}</p>}
      </div>
    </div>
    {sponsor.offer_title && (
      <div className="sponsor-offer" style={{ background: `${accentColor}08`, borderColor: `${accentColor}20` }}>
        <span style={{ color: accentColor, fontWeight: 700, fontSize: 13 }}>🎁 {sponsor.offer_title}</span>
        {sponsor.offer_value && <span className="offer-value">{sponsor.offer_value}</span>}
      </div>
    )}
    <button className="btn btn-primary sponsor-cta" style={{ background: accentColor }} onClick={() => onGetCode(sponsor)}>
      {/* WT-28: same words as the Wallet's button */}
      <QrCode size={15} /> Get my QR code
    </button>
  </div>
);

export default SponsorsPage;
